import React, { useEffect, useState } from 'react';
import { Mail, X, CheckCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useFamilyGroupStore } from '../../store/familyGroupStore';
import { useFamilyInvitationStore } from '../../store/familyInvitationStore';
import { useAuthStore } from '../../store/authStore';
import Button from '../ui/Button';
import { toast } from 'sonner';

interface PendingInvitation {
  id: string;
  family_group_id: string;
  family_name: string;
  invited_by_name: string;
  role: string;
  token: string;
  expires_at: string;
}

const PendingInvitationsBanner: React.FC = () => {
  const [invitations, setInvitations] = useState<PendingInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const { user } = useAuthStore();
  const { fetchFamilyGroups } = useFamilyGroupStore();
  const { acceptInvitation, setReceivedCount } = useFamilyInvitationStore();

  useEffect(() => {
    loadPendingInvitations();
  }, [user]);

  const loadPendingInvitations = async () => {
    if (!user) return;

    try {
      setLoading(true);

      const { data, error } = await supabase.rpc('get_pending_invitations');

      if (error) {
        console.error('[PendingInvitationsBanner] Error loading invitations:', error);
        return;
      }

      const pendingInvitations: PendingInvitation[] = (data || []).map((inv: any) => ({
        id: inv.id,
        family_group_id: inv.family_group_id,
        family_name: inv.family_name || 'Unknown Family',
        invited_by_name: inv.invited_by_name || 'Someone',
        role: inv.role,
        token: inv.token,
        expires_at: inv.expires_at
      }));

      setInvitations(pendingInvitations);
      // Publish count to the store so usePromptOrchestrator can suppress
      // other prompts while the user has incoming invitations to handle.
      setReceivedCount(pendingInvitations.length);
    } catch (err) {
      console.error('[PendingInvitationsBanner] Exception:', err);
      setReceivedCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (invitation: PendingInvitation) => {
    setAccepting(invitation.id);

    try {
      const result = await acceptInvitation(invitation.token);

      if (result.success) {
        toast.success(`¡Te has unido a ${invitation.family_name}!`);
        // Remove from list immediately
        setInvitations(prev => prev.filter(inv => inv.id !== invitation.id));
        // Refresh family groups
        await fetchFamilyGroups();
        // Reload invitations to ensure banner updates
        await loadPendingInvitations();
      } else {
        toast.error(result.error || 'Error al aceptar la invitación');
      }
    } catch (err) {
      console.error('[PendingInvitationsBanner] Error accepting:', err);
      toast.error('Error al aceptar la invitación');
    } finally {
      setAccepting(null);
    }
  };

  const handleDismiss = (invitationId: string) => {
    setDismissed(prev => new Set([...prev, invitationId]));
  };

  // Don't show banner if loading, no invitations, or user is not logged in
  if (loading || !user || invitations.length === 0) {
    return null;
  }

  // Filter out dismissed invitations
  const visibleInvitations = invitations.filter(inv => !dismissed.has(inv.id));

  if (visibleInvitations.length === 0) {
    return null;
  }

  return (
    <div className="mb-6 space-y-3">
      {visibleInvitations.map(invitation => (
        <div
          key={invitation.id}
          className="bg-rose-50 border-2 border-rose-200 rounded-lg p-4 shadow-sm"
        >
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <Mail className="h-6 w-6 text-rose-600" />
            </div>

            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-rose-900 mb-1">
                Invitación Pendiente
              </h3>
              <p className="text-sm text-rose-800">
                <span className="font-medium">{invitation.invited_by_name}</span> te ha invitado a unirte a{' '}
                <span className="font-medium">{invitation.family_name}</span>
              </p>
              <p className="text-xs text-rose-600 mt-1">
                Expira el {new Date(invitation.expires_at).toLocaleDateString('es-ES', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
              </p>

              <div className="flex items-center space-x-2 mt-3">
                <Button
                  onClick={() => handleAccept(invitation)}
                  disabled={accepting === invitation.id}
                  size="sm"
                  className="bg-rose-600 hover:bg-rose-700 text-white min-h-[36px]"
                >
                  {accepting === invitation.id ? (
                    <>
                      <CheckCircle className="h-4 w-4 mr-1 animate-spin" />
                      Aceptando...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Aceptar Invitación
                    </>
                  )}
                </Button>

                <button
                  onClick={() => handleDismiss(invitation.id)}
                  disabled={accepting === invitation.id}
                  className="text-sm text-rose-700 hover:text-rose-800 underline disabled:opacity-50"
                >
                  Recordar después
                </button>
              </div>
            </div>

            <button
              onClick={() => handleDismiss(invitation.id)}
              disabled={accepting === invitation.id}
              className="flex-shrink-0 text-rose-400 hover:text-rose-600 transition-colors disabled:opacity-50"
              aria-label="Cerrar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default PendingInvitationsBanner;
